import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { AppRoutes } from '@/constants';
import { NavRoute } from '@/types';
import { getUserDefaultHomePage, mergeClasses } from '@/utils';
import { usePermission } from '@/hooks';

import { Loading, ProtectedComponent, SvgIcon } from '@/components/common';

import { NAV_ITEMS } from './FloatingNavBar.constants';
import { INavItem } from './FloatingNavBar.interfaces';

const FloatingNavBar = () => {
  const [hoveredItem, setHoveredItem] = useState<INavItem | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { roles, isLoading } = usePermission();

  const onClick = (item: NavRoute) => {
    navigate(item);
  };

  const isItemActive = (itemId: NavRoute): boolean => {
    if (location.pathname === AppRoutes.HOME) {
      return itemId === getUserDefaultHomePage(roles);
    }
    return location.pathname.startsWith(itemId);
  };

  if (isLoading) return <Loading fullScreen />;

  return (
    <nav className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-[200]">
      <div className="bg-primary-dark rounded-lg p-2 flex items-center gap-2">
        {NAV_ITEMS.map((item: INavItem) => {
          const isActive = isItemActive(item.id);
          const isHovered = hoveredItem?.id === item.id;
          const showText = isActive || isHovered;

          return (
            <ProtectedComponent
              key={item.id}
              permissions={[
                {
                  module: item.module,
                  subModule: item.subModule,
                  action: item.action,
                },
              ]}
            >
              <button
                onClick={() => onClick(item.id)}
                onMouseEnter={() => setHoveredItem(item)}
                onMouseLeave={() => setHoveredItem(null)}
                className={mergeClasses(
                  // Base classes
                  'flex items-center justify-center transition-all duration-500 ease-in-out overflow-hidden backdrop-blur-[20px] border rounded-lg p-2',
                  // State-based classes
                  isActive && 'bg-neutral-0 border-neutral-0 py-2 px-3 gap-2',
                  showText && !isActive && 'bg-opacity-light-30 border-neutral-0 py-2 px-3 gap-2',
                  !showText && !isActive && 'bg-opacity-light-20 border-opacity-light-10',
                )}
              >
                <span
                  className={mergeClasses(
                    // Base classes
                    'flex items-center justify-center flex-shrink-0 transition-colors duration-500 ease-in-out',
                    // Active state logic
                    isActive ? 'text-primary-dark' : 'text-neutral-0',
                  )}
                >
                  <SvgIcon component={item.icon} className="size-6" />
                </span>
                <span
                  className={mergeClasses(
                    // Base classes
                    'text-body-base font-semibold whitespace-nowrap overflow-hidden transition-all duration-500 ease-in-out',
                    // Expansion logic
                    showText ? 'max-w-[300px] opacity-100' : 'max-w-0 opacity-0',
                    // Active state logic
                    isActive ? 'text-primary-dark' : 'text-neutral-0',
                  )}
                >
                  {item.label}
                </span>
              </button>
            </ProtectedComponent>
          );
        })}
      </div>
    </nav>
  );
};

export default FloatingNavBar;
